import React, { useEffect, useState } from "react";
import { Button, Loader } from "components/lib";
import Axios from "axios";

/**
 * Server-proxied XPM client/contact search modal.
 *
 * Props:
 *   open        — boolean, controls visibility
 *   title       — heading shown at the top of the modal
 *   onClose()   — fired when the user dismisses without picking
 *   onSelect(t) — fired with the picked record:
 *                 { kind: 'client'|'contact', ContactId, Name, EmailAddress,
 *                   parent_client_id?, parent_client_name? }
 *   restrictToKind — optional 'client' | 'contact' to filter results client-side
 */
export default function XpmSearchModal({
  open,
  title = "Search XPM",
  onClose,
  onSelect,
  restrictToKind = null,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !query || query.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await Axios.get(
          `/api/entity-onboarding/xpm/search?q=${encodeURIComponent(query)}`
        );
        if (cancelled) return;
        const all = res?.data?.data || [];
        setResults(restrictToKind ? all.filter((r) => r.kind === restrictToKind) : all);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || "Search failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, restrictToKind]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6">
      <div className="w-full max-w-2xl rounded bg-white p-6 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Button small outline text="Close" action={onClose} />
        </div>

        <input
          autoFocus
          className="w-full rounded border px-3 py-2"
          placeholder="Search by name or email (min 2 chars)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {loading && (
          <div className="mt-3">
            <Loader />
          </div>
        )}
        {error && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {results.length === 0 && query.length >= 2 && !loading && !error ? (
            <div className="text-sm text-gray-500">No matches.</div>
          ) : (
            <ul className="divide-y">
              {results.map((r) => (
                <li
                  key={`${r.kind}-${r.ContactId}`}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{r.Name || "(unnamed)"}</div>
                    <div className="text-xs text-gray-500">
                      {r.kind === "client" ? "Client" : "Contact"}
                      {r.EmailAddress ? ` · ${r.EmailAddress}` : ""}
                      {r.parent_client_name ? ` · under ${r.parent_client_name}` : ""}
                    </div>
                  </div>
                  <Button
                    small
                    text="Select"
                    color="blue"
                    action={() => onSelect(r)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
